import { CompanyVatTitle, CpWasteCategoriesTitle } from 'shared-pages/common/constants';

/** Τρόπος μετατροπής τιμής στήλης Excel σε τιμή συστήματος */
export type ImportResolver =
  | 'text'
  | 'number'
  | 'yesno'
  | 'categoryName'
  | 'regionName'
  | 'subregionName'
  | 'wasteCategoryNames';

/** Ορισμός πεδίου εισαγωγής σημείων συλλογής */
export type ImportFieldDef = {
  /** Εσωτερικό όνομα πεδίου (ταιριάζει με CollectionpointInput όπου γίνεται) */
  field: string;
  /** Αποδεκτά ονόματα στήλης — το πρώτο είναι το canonical (για template) */
  labels: string[];
  /** Υποχρεωτική στήλη στο Excel */
  required: boolean;
  /** Περιγραφή για τον χρήστη */
  description: string;
  /** Παράδειγμα τιμής */
  example?: string;
  resolver?: ImportResolver;
  notes?: string;
};

/**
 * Schema εισαγωγής σημείων συλλογής.
 * Ταυτοποίηση γίνεται μόνο με την επωνυμία: αν υπάρχει ενημερώνεται, αλλιώς καταχωρείται νέο.
 */
export const COLLECTIONPOINT_IMPORT_FIELDS: readonly ImportFieldDef[] = [
  {
    field: 'name',
    labels: ['Επωνυμία'],
    required: true,
    description: 'Η επωνυμία του σημείου συλλογής. Χρησιμοποιείται ως μοναδικό κλειδί εισαγωγής.',
    example: 'ΣΥΝΕΡΓΕΙΟ ΠΑΠΑΔΟΠΟΥΛΟΣ ΑΕ',
    resolver: 'text',
    notes:
      'Αν υπάρχει ήδη σημείο με την ίδια επωνυμία ενημερώνεται· αλλιώς δημιουργείται νέο. Πολλαπλά σημεία με την ίδια επωνυμία θεωρούνται σφάλμα.',
  },
  {
    field: 'code',
    labels: ['Κωδικός'],
    required: false,
    description: 'Κωδικός σημείου συλλογής',
    example: 'CP-1025',
    resolver: 'text',
  },
  {
    field: 'addressLine1',
    labels: ['Διεύθυνση'],
    required: true,
    description: 'Οδός και αριθμός',
    example: 'Λεωφ. Κηφισίας 12',
    resolver: 'text',
    notes: 'Υποχρεωτικό μόνο σε νέα καταχώριση. Σε ενημέρωση αφήστε κενό για να μην αλλάξει.',
  },
  {
    field: 'addressLine2',
    labels: ['Διεύθυνση 2', 'Διεύθυνση - 2η γραμμή'],
    required: false,
    description: 'Δεύτερη γραμμή διεύθυνσης',
    resolver: 'text',
  },
  {
    field: 'postalCode',
    labels: ['Τ.Κ.', 'T.K.'],
    required: false,
    description: 'Ταχυδρομικός κώδικας',
    example: '11526',
    resolver: 'text',
  },
  {
    field: 'addressArea',
    labels: ['Περιοχή'],
    required: false,
    description: 'Περιοχή / συνοικία',
    example: 'Αμπελόκηποι',
    resolver: 'text',
  },
  {
    field: 'addressCity',
    labels: ['Πόλη'],
    required: false,
    description: 'Πόλη',
    example: 'Αθήνα',
    resolver: 'text',
  },
  {
    field: 'subregionId',
    labels: ['Περιφερειακή Ενότητα'],
    required: false,
    description: 'Όνομα περιφερειακής ενότητας όπως εμφανίζεται στο σύστημα',
    example: 'Κεντρικού Τομέα Αθηνών',
    resolver: 'subregionName',
    notes: 'Αν συμπληρωθεί, ενημερώνεται και η περιφέρεια όταν λείπει από το Excel.',
  },
  {
    field: 'regionId',
    labels: ['Περιφέρεια'],
    required: false,
    description: 'Όνομα περιφέρειας όπως εμφανίζεται στο σύστημα',
    example: 'Αττική',
    resolver: 'regionName',
  },
  {
    field: 'phone',
    labels: ['Τηλέφωνο'],
    required: true,
    description: 'Τηλέφωνο επικοινωνίας',
    example: '2101234567',
    resolver: 'text',
    notes: 'Υποχρεωτικό μόνο σε νέα καταχώριση. Σε ενημέρωση αφήστε κενό για να μην αλλάξει.',
  },
  {
    field: 'mobile',
    labels: ['Κινητό'],
    required: false,
    description: 'Κινητό τηλέφωνο',
    resolver: 'text',
  },
  {
    field: 'fax',
    labels: ['Φαξ'],
    required: false,
    description: 'Αριθμός φαξ',
    resolver: 'text',
  },
  {
    field: 'email',
    labels: ['Email', 'E-mail'],
    required: false,
    description: 'Διεύθυνση email (πολλαπλά με κόμμα)',
    example: 'info@example.gr',
    resolver: 'text',
  },
  {
    field: 'contactPerson',
    labels: ['Υπεύθυνος'],
    required: false,
    description: 'Ονοματεπώνυμο υπευθύνου',
    example: 'Γιάννης Παπαδόπουλος',
    resolver: 'text',
  },
  {
    field: 'companyVat',
    labels: [CompanyVatTitle, 'ΑΦΜ'],
    required: false,
    description: 'Αριθμός φορολογικού μητρώου',
    example: '094000000',
    resolver: 'text',
  },
  {
    field: 'companyTaxOffice',
    labels: ['Δ.Ο.Υ.', 'ΔΟΥ'],
    required: false,
    description: 'Επωνυμία Δ.Ο.Υ.',
    example: 'Α Αθηνών',
    resolver: 'text',
  },
  {
    field: 'imaCode',
    labels: ['Α.Μ. ΗΜΑ', 'Αριθμός Η.Μ.Α.'],
    required: false,
    description: 'Αριθμός μητρώου ΗΜΑ',
    resolver: 'text',
  },
  {
    field: 'ymeCode',
    labels: ['Α.Μ. Υ.Μ.Ε', 'Α.Μ. Συνεργείου στο Μητρώο του Υ.Μ.Ε'],
    required: false,
    description: 'Αριθμός μητρώου Υ.Μ.Ε. (συνεργεία)',
    resolver: 'text',
  },
  {
    field: 'categoryId',
    labels: ['Κατηγορία', 'Κατηγορία Δραστηριότητας'],
    required: false,
    description: 'Όνομα κατηγορίας δραστηριότητας όπως εμφανίζεται στο σύστημα',
    example: 'Συνεργείο',
    resolver: 'categoryName',
  },
  {
    field: 'wasteCategoryIds',
    labels: [CpWasteCategoriesTitle, 'Κατηγορίες Αποβλήτων'],
    required: false,
    description: 'Ονόματα κατηγοριών χωρισμένα με κόμμα',
    example: 'Li-Ion, Μπαταρίες',
    resolver: 'wasteCategoryNames',
  },
  {
    field: 'notes',
    labels: ['Σχόλια'],
    required: false,
    description: 'Ελεύθερο κείμενο',
    resolver: 'text',
  },
  {
    field: 'isActive',
    labels: ['Ενεργή Εγγραφή'],
    required: false,
    description: 'ΝΑΙ ή ΟΧΙ. Σε νέα καταχώριση, αν λείπει θεωρείται ΝΑΙ.',
    example: 'ΝΑΙ',
    resolver: 'yesno',
  },
  {
    field: 'isPubliclyVisibleInMap',
    labels: ['Ορατό στον Χάρτη'],
    required: false,
    description: 'ΝΑΙ ή ΟΧΙ',
    example: 'ΟΧΙ',
    resolver: 'yesno',
  },
  {
    field: 'legacyAppId',
    labels: ['Κωδικός ERP'],
    required: false,
    description: 'Εξωτερικός / ERP κωδικός. Αν λείπει σε νέα εγγραφή, το σύστημα δημιουργεί uuid.',
    example: '1025',
    resolver: 'text',
  },
  {
    field: 'latitude',
    labels: ['Γεωγραφικό Πλάτος', 'Latitude'],
    required: false,
    description: 'Συντεταγμένη GPS (πλάτος)',
    example: '37.9838',
    resolver: 'number',
  },
  {
    field: 'longitude',
    labels: ['Γεωγραφικό Μήκος', 'Longitude'],
    required: false,
    description: 'Συντεταγμένη GPS (μήκος)',
    example: '23.7275',
    resolver: 'number',
  },
] as const;

/** Canonical τίτλος στήλης (πρώτο label) */
export function canonicalLabel(def: ImportFieldDef): string {
  return def.labels[0];
}

/** Πεδία που εμφανίζονται στο φύλλο δεδομένων του προτύπου */
export function templateDataFields(): ImportFieldDef[] {
  return [...COLLECTIONPOINT_IMPORT_FIELDS];
}
